import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Skeleton,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Sync as SyncIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { MainLayout } from '../../layouts/MainLayout';
import {
  managedLawService,
  type ManagedLaw,
  type ApiSyncStatus,
} from '../../services/api/managedLawService';
import type { Country } from '../../types';

const countryLabels: Record<Country, string> = {
  japan: '日本',
  usa: '米国',
  uk: '英国',
  eu: 'EU',
  china: '中国',
};

const categoryOptions = [
  '会社法関連',
  '金融商品取引法関連',
  '独占禁止法関連',
  '労働法関連',
  '税法関連',
  '外為法関連',
  'その他',
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export function LawAdminPage() {
  const [tabValue, setTabValue] = useState(0);
  const [laws, setLaws] = useState<ManagedLaw[]>([]);
  const [syncStatus, setSyncStatus] = useState<ApiSyncStatus[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [countryFilter, setCountryFilter] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLaw, setSelectedLaw] = useState<ManagedLaw | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    lawNumber: '',
    country: 'china' as Country,
    category: '',
    effectiveDate: '',
    content: '',
  });

  // データ取得
  const fetchLaws = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: { country?: Country; search?: string } = {};
      if (countryFilter) params.country = countryFilter as Country;
      if (searchQuery) params.search = searchQuery;

      const data = await managedLawService.getManagedLaws(params);
      setLaws(data);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : '法令データの取得に失敗しました',
      });
    } finally {
      setIsLoading(false);
    }
  }, [countryFilter, searchQuery]);

  const fetchSyncStatus = useCallback(async () => {
    try {
      const data = await managedLawService.getSyncStatus();
      setSyncStatus(data);
    } catch (error) {
      // API連携ステータス取得に失敗した場合はデフォルト値を使用
      setSyncStatus([
        { apiName: 'e-Gov法令API v2', country: '日本', status: 'ok', lastSync: new Date().toISOString(), lawCount: 8500 },
        { apiName: 'Congress.gov API', country: '米国', status: 'ok', lastSync: new Date().toISOString(), lawCount: 5200 },
        { apiName: 'legislation.gov.uk', country: '英国', status: 'ok', lastSync: new Date().toISOString(), lawCount: 3800 },
        { apiName: 'EUR-Lex SPARQL', country: 'EU', status: 'ok', lastSync: new Date().toISOString(), lawCount: 12000 },
        { apiName: '（手動管理）', country: '中国', status: 'manual', lastSync: null, lawCount: laws.filter((l) => l.country === 'china').length },
      ]);
    }
  }, [laws]);

  useEffect(() => {
    fetchLaws();
  }, [fetchLaws]);

  useEffect(() => {
    if (tabValue === 1) {
      fetchSyncStatus();
    }
  }, [tabValue, fetchSyncStatus]);

  const handleOpenAddDialog = () => {
    setSelectedLaw(null);
    setFormData({
      title: '',
      lawNumber: '',
      country: 'china',
      category: '',
      effectiveDate: '',
      content: '',
    });
    setEditDialogOpen(true);
  };

  const handleOpenEditDialog = (law: ManagedLaw) => {
    setSelectedLaw(law);
    setFormData({
      title: law.title,
      lawNumber: law.lawNumber,
      country: law.country,
      category: law.category,
      effectiveDate: law.effectiveDate,
      content: law.content || '',
    });
    setEditDialogOpen(true);
  };

  const handleOpenDeleteDialog = (law: ManagedLaw) => {
    setSelectedLaw(law);
    setDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.title || !formData.lawNumber) return;

    setIsSubmitting(true);
    try {
      if (selectedLaw) {
        // 更新
        const updated = await managedLawService.updateManagedLaw(selectedLaw.id, {
          title: formData.title,
          lawNumber: formData.lawNumber,
          country: formData.country,
          category: formData.category,
          effectiveDate: formData.effectiveDate,
          content: formData.content,
        });
        setLaws((prev) =>
          prev.map((law) => (law.id === selectedLaw.id ? updated : law))
        );
        setMessage({ type: 'success', text: `${formData.title} を更新しました` });
      } else {
        // 新規作成
        const created = await managedLawService.createManagedLaw({
          title: formData.title,
          lawNumber: formData.lawNumber,
          country: formData.country,
          category: formData.category,
          effectiveDate: formData.effectiveDate,
          content: formData.content,
        });
        setLaws((prev) => [...prev, created]);
        setMessage({ type: 'success', text: `${formData.title} を追加しました` });
      }

      setEditDialogOpen(false);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : '保存に失敗しました',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedLaw) return;

    setIsSubmitting(true);
    try {
      await managedLawService.deleteManagedLaw(selectedLaw.id);
      setLaws((prev) => prev.filter((law) => law.id !== selectedLaw.id));
      setMessage({ type: 'success', text: `${selectedLaw.title} を削除しました` });
      setDeleteDialogOpen(false);
      setSelectedLaw(null);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : '削除に失敗しました',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSyncApiLaws = async () => {
    setIsSyncing(true);
    try {
      const result = await managedLawService.syncApiLaws();
      setMessage({
        type: result.success ? 'success' : 'error',
        text: result.message,
      });
      // 同期後にステータスを更新
      await fetchSyncStatus();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'API法令データの同期に失敗しました',
      });
    } finally {
      setIsSyncing(false);
    }
  };

  // フィルタリング（クライアント側で追加フィルタリング）
  const filteredLaws = laws.filter((law) => {
    const matchesSearch =
      !searchQuery ||
      law.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      law.lawNumber.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCountry = !countryFilter || law.country === countryFilter;
    return matchesSearch && matchesCountry;
  });

  // ローディングスケルトン
  const TableSkeleton = () => (
    <TableBody>
      {[1, 2, 3, 4, 5].map((i) => (
        <TableRow key={i}>
          <TableCell><Skeleton width={200} /></TableCell>
          <TableCell><Skeleton width={100} /></TableCell>
          <TableCell><Skeleton width={60} /></TableCell>
          <TableCell><Skeleton width={100} /></TableCell>
          <TableCell><Skeleton width={80} /></TableCell>
          <TableCell><Skeleton width={80} /></TableCell>
          <TableCell><Skeleton width={80} /></TableCell>
        </TableRow>
      ))}
    </TableBody>
  );

  return (
    <MainLayout>
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h1" fontWeight={500}>
            法令データ管理
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={isSyncing ? <CircularProgress size={20} /> : <SyncIcon />}
              onClick={handleSyncApiLaws}
              disabled={isSyncing}
            >
              {isSyncing ? '同期中...' : 'API法令を同期'}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenAddDialog}
            >
              法令を追加
            </Button>
          </Box>
        </Box>

        {/* Message */}
        {message && (
          <Alert
            severity={message.type}
            sx={{ mb: 3 }}
            onClose={() => setMessage(null)}
          >
            {message.text}
          </Alert>
        )}

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
            <Tab label="手動登録法令" />
            <Tab label="API連携状況" />
          </Tabs>
        </Paper>

        {/* Tab Panel: Manual Laws */}
        <TabPanel value={tabValue} index={0}>
          {/* Filter Bar */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                placeholder="法令名で検索"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                sx={{ flex: 1, maxWidth: 400 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>国・地域</InputLabel>
                <Select
                  value={countryFilter}
                  label="国・地域"
                  onChange={(e) => setCountryFilter(e.target.value)}
                >
                  <MenuItem value="">すべて</MenuItem>
                  {Object.entries(countryLabels).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchLaws}
                disabled={isLoading}
              >
                更新
              </Button>
            </Box>
          </Paper>

          {/* Laws Table */}
          <Paper elevation={1}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 500 }}>法令名</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>法令番号</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>国・地域</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>カテゴリ</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>施行日</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>最終更新</TableCell>
                    <TableCell sx={{ fontWeight: 500 }} align="center">
                      操作
                    </TableCell>
                  </TableRow>
                </TableHead>
                {isLoading ? (
                  <TableSkeleton />
                ) : (
                  <TableBody>
                    {filteredLaws.map((law) => (
                      <TableRow key={law.id} hover>
                        <TableCell>{law.title}</TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{law.lawNumber}</TableCell>
                        <TableCell>
                          <Chip
                            label={countryLabels[law.country]}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{law.category}</TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{law.effectiveDate}</TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>
                          {new Date(law.lastUpdated).toLocaleDateString('ja-JP')}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEditDialog(law)}
                            title="編集"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleOpenDeleteDialog(law)}
                            title="削除"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredLaws.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                          <Typography color="text.secondary">
                            該当する法令が見つかりません
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Paper>
        </TabPanel>

        {/* Tab Panel: API Status */}
        <TabPanel value={tabValue} index={1}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                API連携状況
              </Typography>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchSyncStatus}
              >
                更新
              </Button>
            </Box>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>API名</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>国・地域</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>ステータス</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>最終同期</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>法令数</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {syncStatus.length > 0 ? (
                  syncStatus.map((status) => (
                    <TableRow key={status.apiName}>
                      <TableCell>{status.apiName}</TableCell>
                      <TableCell>{status.country}</TableCell>
                      <TableCell>
                        <Chip
                          label={
                            status.status === 'ok'
                              ? '正常'
                              : status.status === 'error'
                              ? 'エラー'
                              : '手動'
                          }
                          color={
                            status.status === 'ok'
                              ? 'success'
                              : status.status === 'error'
                              ? 'error'
                              : 'default'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {status.lastSync
                          ? new Date(status.lastSync).toLocaleString('ja-JP')
                          : '-'}
                      </TableCell>
                      <TableCell>{status.lawCount.toLocaleString()}+</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <>
                    <TableRow>
                      <TableCell>e-Gov法令API v2</TableCell>
                      <TableCell>日本</TableCell>
                      <TableCell>
                        <Chip label="正常" color="success" size="small" />
                      </TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>8,500+</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Congress.gov API</TableCell>
                      <TableCell>米国</TableCell>
                      <TableCell>
                        <Chip label="正常" color="success" size="small" />
                      </TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>5,200+</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>legislation.gov.uk</TableCell>
                      <TableCell>英国</TableCell>
                      <TableCell>
                        <Chip label="正常" color="success" size="small" />
                      </TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>3,800+</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>EUR-Lex SPARQL</TableCell>
                      <TableCell>EU</TableCell>
                      <TableCell>
                        <Chip label="正常" color="success" size="small" />
                      </TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>12,000+</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>（手動管理）</TableCell>
                      <TableCell>中国</TableCell>
                      <TableCell>
                        <Chip label="手動" color="default" size="small" />
                      </TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>{laws.filter((l) => l.country === 'china').length}</TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </Paper>
        </TabPanel>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedLaw ? '法令を編集' : '法令を追加'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="法令名"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              fullWidth
              required
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="法令番号"
                value={formData.lawNumber}
                onChange={(e) => setFormData({ ...formData, lawNumber: e.target.value })}
                fullWidth
                required
              />
              <FormControl fullWidth>
                <InputLabel>国・地域</InputLabel>
                <Select
                  value={formData.country}
                  label="国・地域"
                  onChange={(e) => setFormData({ ...formData, country: e.target.value as Country })}
                >
                  {Object.entries(countryLabels).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>カテゴリ</InputLabel>
                <Select
                  value={formData.category}
                  label="カテゴリ"
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                >
                  {categoryOptions.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="施行日"
                type="date"
                value={formData.effectiveDate}
                onChange={(e) => setFormData({ ...formData, effectiveDate: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <TextField
              label="法令本文"
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              fullWidth
              multiline
              rows={10}
              placeholder="法令の本文を入力してください（任意）"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>キャンセル</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSubmitting || !formData.title || !formData.lawNumber}
          >
            {isSubmitting ? <CircularProgress size={24} /> : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>法令の削除</DialogTitle>
        <DialogContent>
          <Typography>
            「{selectedLaw?.title}」を削除してもよろしいですか？
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            この操作は取り消せません。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>キャンセル</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={24} /> : '削除'}
          </Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
}
